/**
 * Biogas Profile Page - Clean Personal Information Display
 *
 * Features:
 * - Real-time data fetching from backend on page load
 * - Clean Personal Details tab with all user information
 * - Dynamic header with Name, Phone, Profile Picture, and Role
 * - Proper loading states and error handling
 * - Auto-refresh after updates
 * - No hardcoded/dummy data
 *
 * Data Flow: Backend API → Component State → UI Display
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Loader2,
  AlertCircle,
  User,
  Phone,
  Mail,
  Shield,
  Calendar,
  Building2,
  MapPin,
  Users,
  TrendingUp,
  Droplets,
  Zap,
  DollarSign,
  CheckCircle,
  CheckCircle2,
  Clock,
  Edit3,
  Save,
  X,
  Camera,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getBiogasProfile,
  updatePersonDetails,
  BiogasProfileResponse,
  PersonDetails,
  BiogasClusterDetails,
  SummaryMetrics,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatDate,
  formatDateTime,
} from '@/services/biogas/profileService';
import ProfileDataGrid, { DataGridItem } from '@/pages/gaushala/components/ProfileDataGrid';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Biogas Profile Component
 */
export const BiogasProfile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [profile, setProfile] = useState<BiogasProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [personDetails, setPersonDetails] = useState<PersonDetails | null>(null);
  const [clusters, setClusters] = useState<BiogasClusterDetails[]>([]);
  const [summary, setSummary] = useState<SummaryMetrics | null>(null);

  // Edit mode state
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editData, setEditData] = useState<Partial<PersonDetails> | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<string>('personal');
  const [activeClusterTab, setActiveClusterTab] = useState<string>('');

  /**
   * Fetch profile on component mount
   */
  useEffect(() => {
    fetchProfile();
  }, []);

  /**
   * Set active cluster tab when clusters load
   */
  useEffect(() => {
    if (clusters.length > 0 && !activeClusterTab) {
      setActiveClusterTab(clusters[0].clusterCode || '0');
    }
  }, [clusters, activeClusterTab]);

  /**
   * Initialize edit data when entering edit mode
   */
  useEffect(() => {
    if (editMode && personDetails) {
      setEditData({
        name: personDetails.name,
        email: personDetails.email,
        phone: personDetails.phone,
      });
    }
  }, [editMode, personDetails]);

  /**
   * Fetch complete profile data from backend
   */
  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📡 Fetching profile from backend...');
      const profileData = await getBiogasProfile();

      console.log('✅ Profile data received:', profileData);
      console.log('📋 PersonDetails:', profileData.personDetails);
      console.log('🏭 Clusters:', profileData.clusters);
      console.log('📊 Summary:', profileData.summary);

      // Validate response data
      if (!profileData) {
        throw new Error('Invalid response data received from server');
      }

      setProfile(profileData);
      setPersonDetails(profileData.personDetails || null);
      setClusters(profileData.clusters || []);
      setSummary(profileData.summary || null);

      if (profileData.personDetails) {
        toast.success(`Profile loaded for ${profileData.personDetails.name || 'User'}`);
      } else {
        console.warn('⚠️ No personDetails in response - profile loaded with minimal data');
      }
    } catch (err) {
      let errorMessage = 'Failed to load profile';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      // Provide more specific error messages
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        errorMessage = 'Your session has expired. Please login again.';
      } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        errorMessage = 'Profile not found. Please contact support.';
      } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
      toast.error(errorMessage);
      console.error('❌ Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle edit mode toggle
   */
  const handleEditToggle = () => {
    if (editMode) {
      // Cancel edit
      setEditMode(false);
      setEditData(null);
      setSaveError(null);
    } else {
      // Enter edit mode
      setEditMode(true);
    }
  };

  /**
   * Handle profile page close - navigate back to dashboard
   */
  const handleCloseProfile = () => {
    navigate(-1); // Go back to previous page
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Validate edit data
   */
  const validateEditData = (): boolean => {
    if (!editData?.name || !editData?.name.trim()) {
      setSaveError('Name is required');
      return false;
    }
    if (!editData?.email || !editData?.email.trim()) {
      setSaveError('Email is required');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editData.email)) {
      setSaveError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  /**
   * Save personal details to backend
   */
  const handleSavePersonalDetails = async () => {
    setSaveError(null);

    if (!validateEditData()) {
      return;
    }

    try {
      setIsSaving(true);

      const updatedDetails = await updatePersonDetails(editData || {});

      // Update local state with new data
      setPersonDetails(updatedDetails);
      setEditMode(false);
      setEditData(null);

      toast.success('Profile updated successfully');

      // Refresh full profile to get updated data
      await fetchProfile();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save profile';
      setSaveError(errorMessage);
      toast.error(errorMessage);
      console.error('Error saving profile:', err);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Get current cluster details
   */
  const getCurrentCluster = (): BiogasClusterDetails | null => {
    return clusters.find(c => c.clusterCode === activeClusterTab) || null;
  };

  /**
   * Build Personal Details data for display
   */
  const getPersonalDetailsData = (): DataGridItem[] => {
    if (!personDetails) return [];
    return [
      ...(personDetails.name ? [{ label: 'Full Name', value: personDetails.name, icon: <User className="w-4 h-4" /> }] : []),
      ...(personDetails.phone ? [{ label: 'Phone', value: personDetails.phone, icon: <Phone className="w-4 h-4" /> }] : []),
      ...(personDetails.email ? [{ label: 'Email', value: personDetails.email, icon: <Mail className="w-4 h-4" /> }] : []),
      ...(personDetails.kycStatus ? [{ label: 'KYC Status', value: personDetails.kycStatus, icon: <Shield className="w-4 h-4" /> }] : []),
      ...(personDetails.primaryRole ? [{ label: 'Primary Role', value: personDetails.primaryRole, icon: <Shield className="w-4 h-4" /> }] : []),
      ...(personDetails.createdAt ? [{ label: 'Account Created', value: personDetails.createdAt, icon: <Calendar className="w-4 h-4" />, format: formatDate }] : []),
      ...(personDetails.lastLogin ? [{ label: 'Last Login', value: personDetails.lastLogin, icon: <Calendar className="w-4 h-4" />, format: formatDateTime }] : []),
    ];
  };

  /**
   * Build Cluster Details data
   */
  const getClusterDetailsData = (cluster: BiogasClusterDetails): DataGridItem[] => {
    if (!cluster) return [];
    return [
      ...(cluster.clusterCode ? [{ label: 'Cluster Code', value: cluster.clusterCode, icon: <Building2 className="w-4 h-4" /> }] : []),
      ...(cluster.clusterName ? [{ label: 'Cluster Name', value: cluster.clusterName, icon: <Building2 className="w-4 h-4" /> }] : []),
      ...(cluster.locationAddress ? [{ label: 'Address', value: cluster.locationAddress, icon: <MapPin className="w-4 h-4" /> }] : []),
      ...(cluster.district ? [{ label: 'District', value: cluster.district }] : []),
      ...(cluster.state ? [{ label: 'State', value: cluster.state }] : []),
      ...(cluster.pincode ? [{ label: 'Pincode', value: cluster.pincode }] : []),
      ...(cluster.totalCapacityM3 ? [{ label: 'Total Capacity', value: cluster.totalCapacityM3, icon: <Droplets className="w-4 h-4" />, format: (v) => `${formatNumber(v)} m³` }] : []),
      ...(cluster.totalActiveFarmers ? [{ label: 'Active Farmers', value: cluster.totalActiveFarmers, icon: <Users className="w-4 h-4" />, format: formatNumber }] : []),
    ];
  };

  /**
   * Loading state
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error && !personDetails) {
    return (
      <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
          <div className="flex-1 px-6 lg:px-8 py-6 lg:py-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-1">Biogas Profile</h1>
            <p className="text-gray-600">Biogas cluster and operational profile</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 flex items-center justify-center">
          <Card className="p-6 lg:p-8 bg-white rounded-xl shadow-md w-full max-w-md">
            <Alert variant="destructive" className="mb-6 border-red-300 bg-red-50 rounded-lg">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <div className="ml-3 flex-1">
                <h3 className="font-semibold text-red-900">Error Loading Profile</h3>
                <p className="text-sm text-red-800 mt-2">{error}</p>
              </div>
            </Alert>
            <Button onClick={fetchProfile} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg">
              <Loader2 className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  /**
   * Main profile display
   */
  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 overflow-hidden px-0 py-4 lg:py-6">
        <div className="w-full h-full flex flex-col gap-4">
          {/* Profile Header Card */}
          <Card className="p-6 lg:p-8 bg-white rounded-lg shadow-sm border border-gray-200 flex-shrink-0 mx-4 sm:mx-6 lg:mx-8 relative">
            {/* Close Button - Top Right Corner */}
            <button
              onClick={handleCloseProfile}
              className="absolute top-4 lg:top-6 right-4 lg:right-6 text-gray-400 hover:text-gray-700 hover:bg-gray-50 p-2 h-9 w-9 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
              title="Close profile"
              aria-label="Close profile"
              tabIndex={0}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Profile Photo */}
              <div className="flex-shrink-0">
                {personDetails?.id ? (
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(personDetails.name || 'User')}&background=3b82f6&color=fff&size=112`}
                    alt={personDetails.name}
                    className="w-28 h-28 rounded-full object-cover border-4 border-blue-600 shadow-lg"
                  />
                ) : (
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center border-4 border-blue-600 shadow-lg">
                    <User className="w-14 h-14 text-blue-600" />
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
                  {personDetails?.name || 'User'}
                </h2>
                <p className="text-gray-500 text-sm font-medium">
                  {personDetails?.primaryRole || 'Biogas Operator'}
                </p>
              </div>
            </div>
          </Card>

          {/* Tabs Section */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col px-4 sm:px-6 lg:px-8">
            {/* Tab List */}
            <div className="mb-0 flex-shrink-0">
              <TabsList className="bg-white border-b border-gray-200 rounded-none p-0 shadow-none w-full justify-start overflow-x-auto">
                <TabsTrigger
                  value="personal"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                >
                  Personal Details
                </TabsTrigger>
                <TabsTrigger
                  value="clusters"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                >
                  Clusters
                </TabsTrigger>
                <TabsTrigger
                  value="summary"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                >
                  Summary
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab Content */}
            <Card className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col overflow-hidden mt-4">
              {/* Personal Tab - Enhanced with Edit Mode */}
              <TabsContent value="personal" className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
                {editMode ? (
                  // Edit Form
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Personal Details</h3>

                      {/* Save Error Alert */}
                      {saveError && (
                        <Alert variant="destructive" className="mb-4 border-red-300 bg-red-50">
                          <AlertCircle className="h-5 w-5" />
                          <div className="ml-3">
                            <p className="text-sm text-red-800">{saveError}</p>
                          </div>
                        </Alert>
                      )}

                      {/* Form Fields */}
                      <div className="space-y-4">
                        {/* Full Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name
                          </label>
                          <input
                            type="text"
                            value={editData?.name || ''}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                            placeholder="Enter your full name"
                          />
                        </div>

                        {/* Email */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={editData?.email || ''}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                            placeholder="Enter your email address"
                          />
                        </div>

                        {/* Phone (Read-only) */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone (Read-only)
                          </label>
                          <input
                            type="tel"
                            value={personDetails?.phone || ''}
                            disabled
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4 border-t border-gray-200">
                      <Button
                        onClick={handleSavePersonalDetails}
                        disabled={isSaving}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 rounded-lg transition-colors"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleEditToggle}
                        disabled={isSaving}
                        variant="outline"
                        className="flex-1 py-2 rounded-lg"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Display Mode - Clean Personal Information Display
                  <>
                    {personDetails ? (
                      <div className="space-y-8">
                        {/* Section 1: Core Personal Information */}
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Personal Information</h3>
                            <p className="text-sm text-gray-600">Your account and profile details</p>
                          </div>
                          <Separator />

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Full Name */}
                            <div className="flex flex-col space-y-3">
                              <div className="flex items-center gap-3">
                                <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Full Name
                                </label>
                              </div>
                              <p className="text-base font-medium text-gray-900">
                                {personDetails.name || '—'}
                              </p>
                              <Separator className="mt-2" />
                            </div>

                            {/* Phone Number */}
                            <div className="flex flex-col space-y-3">
                              <div className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Phone Number
                                </label>
                              </div>
                              <p className="text-base font-medium text-gray-900">
                                {personDetails.phone || '—'}
                              </p>
                              <Separator className="mt-2" />
                            </div>

                            {/* Email Address */}
                            <div className="flex flex-col space-y-3">
                              <div className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Email Address
                                </label>
                              </div>
                              <p className="text-base font-medium text-gray-900">
                                {personDetails.email || '—'}
                              </p>
                              <Separator className="mt-2" />
                            </div>

                            {/* KYC Status */}
                            <div className="flex flex-col space-y-3">
                              <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  KYC Status
                                </label>
                              </div>
                              <Badge variant={personDetails.kycStatus === 'Verified' ? 'default' : personDetails.kycStatus === 'Pending' ? 'secondary' : 'outline'} className="w-fit">
                                {personDetails.kycStatus || 'Pending'}
                              </Badge>
                              <Separator className="mt-2" />
                            </div>

                            {/* Account Created */}
                            {personDetails.createdAt && (
                              <div className="flex flex-col space-y-3">
                                <div className="flex items-center gap-3">
                                  <Calendar className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Account Created
                                  </label>
                                </div>
                                <p className="text-base font-medium text-gray-900">
                                  {formatDate(personDetails.createdAt)}
                                </p>
                                <Separator className="mt-2" />
                              </div>
                            )}

                            {/* Last Login */}
                            {personDetails.lastLogin && (
                              <div className="flex flex-col space-y-3">
                                <div className="flex items-center gap-3">
                                  <Clock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Last Login
                                  </label>
                                </div>
                                <p className="text-base font-medium text-gray-900">
                                  {formatDateTime(personDetails.lastLogin)}
                                </p>
                                <Separator className="mt-2" />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Section 2: Role & Account Information */}
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Role & Access Information</h3>
                            <p className="text-sm text-gray-600">Your assigned roles and permissions</p>
                          </div>
                          <Separator />

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Primary Role */}
                            {personDetails.primaryRole && (
                              <div className="flex flex-col space-y-3">
                                <div className="flex items-center gap-3">
                                  <Shield className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Primary Role
                                  </label>
                                </div>
                                <Badge className="bg-indigo-600 text-white px-2.5 py-0.5 text-xs w-fit">
                                  {personDetails.primaryRole}
                                </Badge>
                                <Separator className="mt-2" />
                              </div>
                            )}

                            {/* Account Status */}
                            <div className="flex flex-col space-y-3">
                              <div className="flex items-center gap-3">
                                <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Account Status
                                </label>
                              </div>
                              <Badge className={personDetails.isActive ? 'bg-green-600 text-white px-2.5 py-0.5 text-xs w-fit' : 'bg-gray-500 text-white px-2.5 py-0.5 text-xs w-fit'}>
                                {personDetails.isActive ? '✓ Active' : 'Inactive'}
                              </Badge>
                              <Separator className="mt-2" />
                            </div>
                          </div>
                        </div>

                        {/* Section 3: Assigned Roles */}
                        {personDetails.roles && personDetails.roles.length > 0 && (
                          <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                              <Users className="w-5 h-5 mr-2 text-blue-600" />
                              All Assigned Roles ({personDetails.roles.length})
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {personDetails.roles.map((role, idx) => (
                                <Badge
                                  key={idx}
                                  className="bg-blue-100 text-blue-800 border border-blue-300 px-3 py-1 text-xs font-medium"
                                >
                                  {role}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Section 4: Allowed Services */}
                        {personDetails.allowedServices && personDetails.allowedServices.length > 0 && (
                          <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                              <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
                              Allowed Services ({personDetails.allowedServices.length})
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {personDetails.allowedServices.map((service, idx) => (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="border border-gray-300 bg-white text-gray-800 px-3 py-1 text-xs font-medium hover:bg-gray-50"
                                >
                                  {service}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Section 5: Account Metadata */}
                        {(personDetails.createdAt || personDetails.lastLogin) && (
                          <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                              Account Timeline
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {personDetails.createdAt && (
                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Account Created</p>
                                  <p className="text-sm text-gray-900 font-semibold">
                                    {formatDate(personDetails.createdAt)}
                                  </p>
                                </div>
                              )}

                              {personDetails.lastLogin && (
                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Last Login</p>
                                  <p className="text-sm text-gray-900 font-semibold">
                                    {formatDateTime(personDetails.lastLogin)}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Alert className="border-2 border-red-300 bg-red-50 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <div className="ml-4">
                          <h3 className="font-bold text-red-900 text-lg">Unable to Load Personal Details</h3>
                          <p className="text-sm text-red-800 mt-2">
                            There was an error loading your personal information. Please try refreshing the page or contact support.
                          </p>
                        </div>
                      </Alert>
                    )}
                  </>
                )}
              </TabsContent>

              {/* Clusters Tab */}
              <TabsContent value="clusters" className="p-6 lg:p-8 overflow-y-auto flex-1 flex flex-col h-full">
                {clusters.length > 0 ? (
                  <>
                    {/* Cluster Sub-tabs */}
                    <div className="mb-6 -mx-6 -mt-8 px-6 pt-8 border-b border-gray-200 bg-gray-50">
                      <Tabs value={activeClusterTab} onValueChange={setActiveClusterTab} className="w-full">
                        <TabsList className="bg-transparent border-0 p-0 shadow-none w-full justify-start space-x-1 rounded-none overflow-x-auto">
                          {clusters.map((cluster) => (
                            <TabsTrigger
                              key={cluster.clusterCode}
                              value={cluster.clusterCode || ''}
                              className="rounded-t-lg border-0 border-b-2 border-transparent data-[state=active]:border-b-blue-600 data-[state=active]:bg-white px-4 py-2 whitespace-nowrap"
                            >
                              {cluster.clusterName || cluster.clusterCode}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                      </Tabs>
                    </div>

                    {/* Cluster Details */}
                    {getCurrentCluster() && (
                      <div className="flex flex-col flex-1 gap-6">
                        {/* Cluster Header Info */}
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-4">{getCurrentCluster()?.clusterName}</h3>
                          <ProfileDataGrid
                            title="Cluster Information"
                            subtitle="Details about this biogas cluster"
                            items={getClusterDetailsData(getCurrentCluster()!)}
                            columns={2}
                          />
                        </div>

                        {/* Cluster Stats Cards */}
                        {getCurrentCluster()?.currentBatchStatus && (
                          <div className="mt-8 pt-8 border-t border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Production</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm text-gray-600 font-medium">Batch Status</p>
                                    <p className="text-lg font-bold text-green-900 mt-1">
                                      {getCurrentCluster()?.currentBatchStatus}
                                    </p>
                                  </div>
                                  <CheckCircle className="w-8 h-8 text-green-600" />
                                </div>
                              </Card>

                              {getCurrentCluster()?.currentBatchProduction && (
                                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-sm text-gray-600 font-medium">Production (m³)</p>
                                      <p className="text-lg font-bold text-blue-900 mt-1">
                                        {formatNumber(getCurrentCluster()?.currentBatchProduction)}
                                      </p>
                                    </div>
                                    <Droplets className="w-8 h-8 text-blue-600" />
                                  </div>
                                </Card>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <Alert className="border-blue-300 bg-blue-50">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                    <div className="ml-3">
                      <h3 className="font-semibold text-blue-900">No Clusters Assigned</h3>
                      <p className="text-sm text-blue-800 mt-1">
                        You have not been assigned to any biogas clusters yet.
                      </p>
                    </div>
                  </Alert>
                )}
              </TabsContent>

              {/* Summary Tab */}
              <TabsContent value="summary" className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
                {summary ? (
                  <div className="space-y-8">
                    {/* Production Metrics */}
                    {summary.production && (
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">Production Metrics</h3>
                          <p className="text-sm text-gray-600">Biogas production and operational efficiency</p>
                        </div>
                        <Separator />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {summary.production.totalBatches !== null && summary.production.totalBatches !== undefined && (
                            <SummaryDataRow label="Total Batches" value={formatNumber(summary.production.totalBatches)} />
                          )}
                          {summary.production.activeBatches !== null && summary.production.activeBatches !== undefined && (
                            <SummaryDataRow label="Active Batches" value={formatNumber(summary.production.activeBatches)} />
                          )}
                          {summary.production.totalBiogasProduced30Days !== null && summary.production.totalBiogasProduced30Days !== undefined && (
                            <SummaryDataRow label="Biogas Produced (30d)" value={`${formatNumber(summary.production.totalBiogasProduced30Days)} m³`} />
                          )}
                          {summary.production.productionEfficiencyPercent !== null && summary.production.productionEfficiencyPercent !== undefined && (
                            <SummaryDataRow label="Efficiency" value={formatPercent(summary.production.productionEfficiencyPercent)} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Collection Metrics */}
                    {summary.collection && (
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">Collection Metrics</h3>
                          <p className="text-sm text-gray-600">Dung collection and farmer contribution data</p>
                        </div>
                        <Separator />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {summary.collection.totalDungCollected30Days !== null && summary.collection.totalDungCollected30Days !== undefined && (
                            <SummaryDataRow label="Dung Collected (30d)" value={`${formatNumber(summary.collection.totalDungCollected30Days)} kg`} />
                          )}
                          {summary.collection.activeContributors !== null && summary.collection.activeContributors !== undefined && (
                            <SummaryDataRow label="Active Farmers" value={formatNumber(summary.collection.activeContributors)} />
                          )}
                          {summary.collection.avgCollectionPerFarmer !== null && summary.collection.avgCollectionPerFarmer !== undefined && (
                            <SummaryDataRow label="Average per Farmer" value={`${formatNumber(summary.collection.avgCollectionPerFarmer)} kg`} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Financial Summary */}
                    {summary.financial && (
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">Financial Summary</h3>
                          <p className="text-sm text-gray-600">Payments and transaction performance</p>
                        </div>
                        <Separator />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {summary.financial.totalPayments30Days !== null && summary.financial.totalPayments30Days !== undefined && (
                            <SummaryDataRow label="Payments (30d)" value={formatCurrency(summary.financial.totalPayments30Days)} />
                          )}
                          {summary.financial.avgPaymentPerFarmer !== null && summary.financial.avgPaymentPerFarmer !== undefined && (
                            <SummaryDataRow label="Average Payment" value={formatCurrency(summary.financial.avgPaymentPerFarmer)} />
                          )}
                          {summary.financial.paymentSuccessRate !== null && summary.financial.paymentSuccessRate !== undefined && (
                            <SummaryDataRow label="Success Rate" value={formatPercent(summary.financial.paymentSuccessRate)} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Subsidy & Carbon Credits */}
                    {summary.subsidy && (
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">Subsidy & Carbon Credits</h3>
                          <p className="text-sm text-gray-600">Government subsidy and environmental credits</p>
                        </div>
                        <Separator />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {summary.subsidy.farmersEligibleForSubsidy !== null && summary.subsidy.farmersEligibleForSubsidy !== undefined && (
                            <SummaryDataRow label="Eligible Farmers" value={formatNumber(summary.subsidy.farmersEligibleForSubsidy)} />
                          )}
                          {summary.subsidy.approvedSubsidyAmount !== null && summary.subsidy.approvedSubsidyAmount !== undefined && (
                            <SummaryDataRow label="Approved Subsidy" value={formatCurrency(summary.subsidy.approvedSubsidyAmount)} />
                          )}
                          {summary.subsidy.carbonCreditsGeneratedYTD !== null && summary.subsidy.carbonCreditsGeneratedYTD !== undefined && (
                            <SummaryDataRow label="Carbon Credits (YTD)" value={`${formatNumber(summary.subsidy.carbonCreditsGeneratedYTD)} credits`} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Quality Metrics */}
                    {summary.quality && (
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">Quality Metrics</h3>
                          <p className="text-sm text-gray-600">Biogas quality and grade distribution</p>
                        </div>
                        <Separator />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {summary.quality.avgMethaneContent !== null && summary.quality.avgMethaneContent !== undefined && (
                            <SummaryDataRow label="Average Methane Content" value={formatPercent(summary.quality.avgMethaneContent)} />
                          )}
                          {summary.quality.premiumGradePercent !== null && summary.quality.premiumGradePercent !== undefined && (
                            <SummaryDataRow label="Premium Grade Percentage" value={formatPercent(summary.quality.premiumGradePercent)} />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Alert className="border-blue-300 bg-blue-50">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                    <div className="ml-3">
                      <h3 className="font-semibold text-blue-900">No Summary Data</h3>
                      <p className="text-sm text-blue-800 mt-1">
                        Summary metrics will appear once production data is available.
                      </p>
                    </div>
                  </Alert>
                )}
              </TabsContent>
            </Card>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

/**
 * Summary Data Row Component
 * Displays a clean label-value pair for summary metrics
 */
interface SummaryDataRowProps {
  label: string;
  value: string;
}

const SummaryDataRow: React.FC<SummaryDataRowProps> = ({ label, value }) => {
  return (
    <div className="flex flex-col space-y-3">
      {/* Label */}
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>

      {/* Value */}
      <p className="text-base font-medium text-gray-900">
        {value}
      </p>

      {/* Horizontal divider */}
      <Separator className="mt-2" />
    </div>
  );
};

export default BiogasProfile;
